import { cn } from '@repo/tailwind-config/tailwind.functions'
import { daysOfWeek } from '@repo/toolkit/constants/calendar'
import { StringFormatter } from '@repo/toolkit/string'
import {
  addDays,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek
} from 'date-fns'
import React, { useEffect, useState } from 'react'

import useTranslate from '../../../../../apps/backoffice/src/lib/hooks/use-translate'
import Typography from '../../atoms/typography'
import { BasicCalendarEvent, HeaderData } from './types'
import { getEventForDay } from './utils'

export type BasicCalendarProps = {
  events: BasicCalendarEvent[]
  disabledDates?: Date[]
  disabledDays?: number[]
  allowedDates?: Date[]
  disabledDaysOfWeek?: number[]
  month?: number
  year?: number
  className?: string
  header?: (data: HeaderData) => JSX.Element
  onEventClicked?: (event: BasicCalendarEvent) => void
  onDaySelected?: (day: Date) => void
  onEventsChanged?: (events: BasicCalendarEvent[]) => void
}

const BasicCalendar: React.FC<BasicCalendarProps> = ({
  events,
  allowedDates,
  disabledDates,
  disabledDays,
  disabledDaysOfWeek,
  month,
  year,
  className,
  header,
  onEventClicked,
  onDaySelected,
  onEventsChanged
}) => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [calendarEvents, setCalendarEvents] = useState<BasicCalendarEvent[]>([])
  const [hoveredEventId, setHoveredEventId] = useState<string>()

  const [toggleEvents, setToggleEvents] = useState(false)
  const { currentLanguage, getLocale } = useTranslate()

  // Calculate the start and end of the month
  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)

  // Get the first and last day displayed on the calendar (including previous/next month days)
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }) // Start week on Monday
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 }) // End week on Sunday

  // Generate days for the entire grid
  // Generate days for the entire grid (only remove next month days)
  const days: Date[] = []
  let day = startDate
  while (day <= endDate) {
    // Add the day if it's in the current month or before the current month ends
    days.push(day)
    day = addDays(day, 1)
  }

  // Move to next month
  const nextMonth = () => {
    setCurrentDate(addDays(endOfMonth(currentDate), 1))
  }

  // Move to previous month
  const prevMonth = () => {
    setCurrentDate(addDays(startOfMonth(currentDate), -1))
  }

  const handleCalendarEventsChange = (events: BasicCalendarEvent[]) => {
    setCalendarEvents(events)
  }

  const compareDates = (a: Date, b: Date) => {
    return format(a, 'yyyy-MM-dd') === format(b, 'yyyy-MM-dd')
  }
  const isDisabledDay = (day: Date) =>
    (allowedDates && !allowedDates?.some((date) => compareDates(date, day))) ||
    disabledDates?.some((date) => compareDates(date, day)) ||
    disabledDays?.includes(day.getDate()) ||
    disabledDaysOfWeek?.includes(day.getDay())

  useEffect(() => {
    const calendarEvents = events.map((event) => ({ ...event, position: 1 }))
    handleCalendarEventsChange([...calendarEvents])
  }, [events])

  useEffect(() => {
    if (toggleEvents) {
      onEventsChanged?.(calendarEvents)
      setToggleEvents(false)
    }
  }, [toggleEvents])

  useEffect(() => {
    if (month !== undefined) setCurrentDate(new Date(year ?? new Date().getFullYear(), month, 1))
  }, [month, year])

  return (
    <div className={cn('overflow-hidden bg-white', className)}>
      {header?.({
        month: StringFormatter.capitalize(format(currentDate, 'MMMM', { locale: getLocale() })),
        year: StringFormatter.capitalize(format(currentDate, 'yyyy', { locale: getLocale() })),
        onPrevMonthClick: prevMonth,
        onNextMonthClick: nextMonth
      })}
      {/* Header with month and navigation */}
      <div className="h-4" />
      <div className="grid grid-cols-7">
        {daysOfWeek[currentLanguage].map((dayOfWeek) => (
          <div className="text-center" key={dayOfWeek}>
            <Typography variant="tag" className="text-grey-lighter">
              {dayOfWeek.slice(0, 3)}
            </Typography>
          </div>
        ))}
      </div>
      <div className="h-1" />

      {/* Grid for the days of the month */}
      <div className="grid grid-cols-7 auto-rows-auto overflow-hidden">
        {/* remove the days of the next month */}
        {days.map((day) => (
          <div
            // return the date as string '2021-08-01'
            key={`cell-${format(day, 'yyyy-MM-dd')}`}
            className={cn(`h-9 relative flex items-center justify-center`, {
              'bg-primary-light': isToday(day),
              'bg-white-active': isDisabledDay(day)
            })}
            onClick={() => {
              if (isDisabledDay(day)) return
              onDaySelected?.(day)
            }} // Handle click
          >
            {/* Day number with red color */}
            <Typography
              variant="secondary-body"
              className={cn('z-10', {
                'text-grey-light': isDisabledDay(day),
                'text-grey-light-hover': !isSameMonth(day, currentDate)
              })}
              style={{ color: getEventForDay(calendarEvents, day)?.style?.dayColor }}
            >
              {format(day, 'd')}
            </Typography>

            {/* Events */}
            {calendarEvents
              .filter(
                (event) =>
                  compareDates(day, event.startDate) ||
                  compareDates(day, event.endDate) ||
                  (isAfter(day, event.startDate) && isBefore(day, event.endDate))
              )
              .map((event) => {
                const daysFromStart = differenceInCalendarDays(day, event.startDate)
                const daysToEnd = differenceInCalendarDays(event.endDate, event.startDate)
                const isHoveredEvent = hoveredEventId === event.id

                // Render event only on the first day, and span across remaining days
                return daysFromStart >= 0 ? (
                  <div
                    onClick={() => {
                      onEventClicked?.(event)
                      event.onClick?.()
                    }}
                    key={event.id}
                    className={cn(
                      `p-2 absolute h-8 flex items-center overflow-visible transition-colors duration-150`,
                      {
                        'rounded-l-full': daysFromStart === 0,
                        'rounded-r-full': daysFromStart === daysToEnd
                      }
                    )}
                    style={{
                      width: '100%',
                      backgroundColor:
                        isHoveredEvent && event.style?.borderColor
                          ? `color-mix(in srgb, ${event.style.backgroundColor} 82%, ${event.style.borderColor} 18%)`
                          : event.style?.backgroundColor,
                      borderStyle: event.style?.borderStyle,
                      borderColor: event.style?.borderColor,
                      borderTopWidth: '2px',
                      borderBottomWidth: '2px',
                      borderLeftWidth: daysFromStart === 0 ? '2px' : '0',
                      borderRightWidth: daysFromStart === daysToEnd ? '2px' : '0'
                    }}
                    onMouseEnter={() => setHoveredEventId(event.id)}
                    onMouseLeave={() => setHoveredEventId(undefined)}
                  />
                ) : null // Don't render the event again in subsequent cells
              })}
          </div>
        ))}
      </div>
    </div>
  )
}

export default BasicCalendar
